import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import dayjs from 'dayjs';
import useInternalBooking from '../hooks/useInternalBooking';
import { toast } from 'react-toastify';

const BookingContext = createContext();

export function BookingProvider({ children }) {
  const [selectedDate, setSelectedDate] = useState(() => dayjs());
  const [previousSelectedDate, setPreviousSelectedDate] = useState(() => dayjs());
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedUpsells, setSelectedUpsells] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedEntertainment, setSelectedEntertainment] = useState(null);
  const [selectedEntertainmentObject, setSelectedEntertainmentObject] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedPricingId, setSelectedPricingId] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [entertainmentError, setEntertainmentError] = useState(null);
  const [tempReservationId, setTempReservationId] = useState(null);
  const [priceOverride, setPriceOverride] = useState(false);
  const [overbooking, setOverbooking] = useState(false);
  const [minPersons, setMinPersons] = useState(null);
  const [maxAvailability, setMaxAvailability] = useState(Infinity);
  const [maxCapacity, setMaxCapacity] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [priceOverrides, setPriceOverrides] = useState({});
  const [specialLineItems, setSpecialLineItems] = useState([]);
  const [customerData, setCustomerData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    street: '',
    postcode: '',
    city: '',
    birthdate: '',
    mail: '',
    companyName: '',
  });
  // Bumped whenever customer data is replaced wholesale from an external source
  // (e.g. copying data from a present customer), so the CustomerData form can resync its local inputs.
  const [customerDataVersion, setCustomerDataVersion] = useState(0);
  // Pre-filled booking comment injected from a user's configured default booking data.
  // The Overview comment field reads this to seed its (otherwise empty) value.
  const [defaultComment, setDefaultComment] = useState('');

  // Replace the whole customer data set, filling unspecified fields with empty defaults.
  const applyCustomerData = useCallback(data => {
    setCustomerData({
      firstName: '',
      lastName: '',
      phone: '',
      street: '',
      postcode: '',
      city: '',
      birthdate: '',
      mail: '',
      companyName: '',
      ...(data || {}),
    });
    setCustomerDataVersion(version => version + 1);
  }, []);
  const [categoryPrices, setCategoryPrices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [upsells, setUpsells] = useState([]);
  const [timeTags, setTimeTags] = useState([]);
  const [packageTags, setPackageTags] = useState([]);
  const [products, setProducts] = useState([]);
  const [resources, setResources] = useState([]);
  const [productResourcesMap, setProductResourcesMap] = useState(new Map());
  const [isPackagesLoading, setIsPackagesLoading] = useState(false);
  const [isUpsellsLoading, setIsUpsellsLoading] = useState(false);

  const { getPackages, getUpsells } = useInternalBooking();

  const fetchPackages = async (tags = null) => {
    setIsPackagesLoading(true);
    try {
      const tagsToUse = tags || getCurrentTags();
      const packagesData = await getPackages(selectedProduct, tagsToUse, selectedEntertainment);
      setPackages(packagesData || []);
      return packagesData || [];
    } catch (error) {
      toast.error('Fehler beim Laden der Pakete');
      console.error('Error fetching packages:', error);
      return [];
    } finally {
      setIsPackagesLoading(false);
    }
  };

  const fetchUpsells = async (tags = null) => {
    setIsUpsellsLoading(true);
    try {
      const tagsToUse = tags || getCurrentTags();
      const upsellsData = await getUpsells(selectedProduct, tagsToUse, selectedEntertainment);
      setUpsells(upsellsData || []);
      return upsellsData || [];
    } catch (error) {
      toast.error('Fehler beim Laden der Upsells');
      console.error('Error fetching upsells:', error);
      return [];
    } finally {
      setIsUpsellsLoading(false);
    }
  };

  const getCurrentTags = (excludeTimeTags = false, excludePackageTags = false) => {
    if (excludeTimeTags) {
      return packageTags;
    } else if (excludePackageTags) {
      return timeTags;
    } else {
      return timeTags.concat(packageTags);
    }
  };

  const buildLineItems = useCallback(() => {
    const computed = [];

    const sumPersons = selectedCategories.reduce(
      (sum, cat) => sum + (parseInt(String(cat.quantity), 10) || 0),
      0
    );
    const laneDensityRaw = Number(selectedEntertainmentObject?.participantLaneDensity || 0);
    const laneDensity = Number.isFinite(laneDensityRaw) && laneDensityRaw > 0 ? laneDensityRaw : null;
    const sumLanes = laneDensity ? Math.ceil(sumPersons / laneDensity) : sumPersons;
    const selectedProductObject = products.find(p => p._id === selectedProduct);
    const selectedPricing = selectedProductObject?.pricing?.find(p => p._id === selectedPricingId);

    selectedCategories.forEach(cat => {
      let price = 0;
      if (selectedPricing && selectedPricing.priceMatrix) {
        const matrixEntry = selectedPricing.priceMatrix.find(
          entry => entry.category === cat.name && entry.durationMS === selectedDuration
        );
        if (matrixEntry && Array.isArray(matrixEntry.prices)) {
          const sortedPrices = [...matrixEntry.prices].sort((a, b) => b.minPersons - a.minPersons);
          const foundTier = sortedPrices.find(tier => sumPersons >= tier.minPersons);
          price = foundTier
            ? foundTier.price
            : sortedPrices.length > 0
              ? sortedPrices[sortedPrices.length - 1].price
              : 0;
        } else if (matrixEntry && typeof matrixEntry.minPrice === 'number') {
          price = matrixEntry.minPrice;
        }
      }
      const override = priceOverrides && priceOverrides[cat.name];
      computed.push({
        type: 'booking',
        referenceId: cat.name,
        description: cat.name,
        categoryName: cat.name,
        count: parseInt(String(cat.quantity), 10) || 0,
        price: override !== undefined ? override : price,
      });
    });

    if (selectedPackage) {
      const pkg = packages.find(p => p.id === selectedPackage.id) || selectedPackage;
      let count = 1;
      if (pkg?.type === 'matchPersons' || pkg?.type === 'matchPersonsNoCancel') {
        count = sumPersons;
      } else if (pkg?.type === 'matchLanesNoCancel') {
        count = sumLanes;
      }
      const override = priceOverrides && priceOverrides[pkg.id];
      computed.push({
        type: 'package',
        referenceId: pkg.id,
        description: pkg.name,
        count,
        price: override !== undefined ? override : pkg.price,
      });
    }

    selectedUpsells.forEach(upsellSel => {
      const upsell = upsells.find(u => u.id === upsellSel.id) || upsellSel;
      const override = priceOverrides && priceOverrides[upsell.id];
      computed.push({
        type: 'upsell',
        referenceId: upsell.id,
        description: upsell.name,
        count: parseInt(String(upsellSel.quantity), 10) || 0,
        price: override !== undefined ? override : upsell.price,
      });
    });

    return computed;
  }, [
    selectedCategories,
    selectedDuration,
    selectedEntertainmentObject,
    selectedPackage,
    selectedPricingId,
    selectedProduct,
    selectedUpsells,
    packages,
    priceOverrides,
    products,
    upsells,
  ]);

  const calculateLineItems = useCallback(() => {
    setLineItems(buildLineItems());
  }, [buildLineItems]);

  const calculateTotalAmount = useCallback(() => {
    const computed = buildLineItems();
    const total = computed.reduce((sum, item) => sum + item.price * item.count, 0);
    const specialTotal = specialLineItems.reduce((sum, item) => sum + item.price * item.count, 0);
    return total + specialTotal;
  }, [buildLineItems, specialLineItems]);

  useEffect(() => {
    setLineItems(buildLineItems());
  }, [buildLineItems]);

  const updateLineItemPrice = useCallback((lineItemId, newPrice) => {
    setPriceOverrides(prev => ({
      ...prev,
      [lineItemId]: newPrice,
    }));
  }, []);

  function updateCapacity(selectedDurationObj, maxCapacity = maxAvailability, minParticipants = 1) {
    const resource = resources.find(r => r._id === selectedResource);
    const selectedProductObject = products.find(p => p._id === selectedProduct);

    const resourceMinPersons = resource?.minPersons ?? 0;
    const resourceMaxPersons = resource?.maxPersons ?? Infinity;

    const durationMinPersons = selectedDurationObj?.minParticipants ?? 0;
    const durationMaxPersons = selectedDurationObj?.maxParticipants ?? Infinity;

    const productMinPersons = selectedProductObject?.minParticipants ?? 0;
    const productMaxPersons = selectedProductObject?.maxParticipants ?? Infinity;

    const finalMin = Math.max(
      resourceMinPersons,
      durationMinPersons,
      productMinPersons,
      minParticipants
    );
    const finalMax = Math.min(
      resourceMaxPersons,
      durationMaxPersons,
      productMaxPersons,
      maxCapacity
    );

    // Only update when both are resolved to stable values to avoid UI flicker
    if (Number.isFinite(finalMin) && Number.isFinite(finalMax)) {
      setMinPersons(finalMin);
      setMaxCapacity(finalMax);
    }
  }

  const resetBooking = useCallback(() => {
    const currentDate = dayjs();
    setPreviousSelectedDate(selectedDate);
    setSelectedDate(currentDate);
    setSelectedTime(null);
    setSelectedDuration(null);
    setSelectedCategories([]);
    setSelectedPackage(null);
    setSelectedUpsells([]);
    setSelectedBranch(null);
    setSelectedEntertainment(null);
    setSelectedEntertainmentObject(null);
    setSelectedProduct(null);
    setSelectedPricingId(null);
    setSelectedResource(null);
    setEntertainmentError(null);
    setTempReservationId(null);
    setPriceOverride(false);
    setOverbooking(false);
    setMinPersons(null);
    setMaxCapacity(null);
    setMaxAvailability(Infinity);
    setPriceOverrides({});
    setSpecialLineItems([]);
    setTimeTags([]);
    setPackageTags([]);
    setResources([]);
    setProductResourcesMap(new Map());
    setCustomerData({
      firstName: '',
      lastName: '',
      phone: '',
      street: '',
      postcode: '',
      city: '',
      birthdate: '',
      mail: '',
      companyName: '',
    });
    setDefaultComment('');
  }, [selectedDate]);

  const getCurrentLineItems = useCallback(() => {
    if (!lineItems.length) return [];
    return lineItems.map(item => ({
      ...item,
      price: priceOverrides[item.referenceId] ?? item.price,
    }));
  }, [lineItems, priceOverrides]);

  const areSpecialLineItemsComplete = useCallback(() => {
    return specialLineItems.every(
      item => item.description.trim() && item.count > 0 && item.price > 0
    );
  }, [specialLineItems]);

  const shouldWaitForResourceSelection = useCallback(
    productId => {
      if (!productId) return false;
      const orderedList = selectedEntertainmentObject?.orderedResources?.resources;
      const hasOrderedResources = Array.isArray(orderedList) && orderedList.length > 0;
      if (hasOrderedResources) {
        return false;
      }
      const resourceCount = productResourcesMap.get(productId);
      return resourceCount > 0 && !selectedResource;
    },
    [productResourcesMap, selectedResource, selectedEntertainmentObject]
  );

  const value = {
    selectedDate,
    setSelectedDate,
    previousSelectedDate,
    setPreviousSelectedDate,
    selectedTime,
    setSelectedTime,
    selectedDuration,
    setSelectedDuration,
    selectedCategories,
    setSelectedCategories,
    selectedPackage,
    setSelectedPackage,
    selectedUpsells,
    setSelectedUpsells,
    selectedBranch,
    setSelectedBranch,
    selectedEntertainment,
    setSelectedEntertainment,
    selectedEntertainmentObject,
    setSelectedEntertainmentObject,
    selectedProduct,
    setSelectedProduct,
    selectedPricingId,
    setSelectedPricingId,
    selectedResource,
    setSelectedResource,
    entertainmentError,
    setEntertainmentError,
    tempReservationId,
    setTempReservationId,
    priceOverride,
    setPriceOverride,
    specialLineItems,
    setSpecialLineItems,
    overbooking,
    setOverbooking,
    minPersons,
    setMinPersons,
    maxCapacity,
    maxAvailability,
    setMaxAvailability,
    setMaxCapacity,
    customerData,
    setCustomerData,
    customerDataVersion,
    applyCustomerData,
    defaultComment,
    setDefaultComment,
    calculateTotalAmount,
    resetBooking,
    lineItems,
    setLineItems,
    priceOverrides,
    setPriceOverrides,
    updateLineItemPrice,
    getCurrentLineItems,
    areSpecialLineItemsComplete,
    shouldWaitForResourceSelection,
    updateCapacity,
    categoryPrices,
    setCategoryPrices,
    packages,
    setPackages,
    upsells,
    setUpsells,
    products,
    setProducts,
    resources,
    setResources,
    productResourcesMap,
    setProductResourcesMap,
    calculateLineItems,
    timeTags,
    setTimeTags,
    packageTags,
    setPackageTags,
    getCurrentTags,
    isPackagesLoading,
    setIsPackagesLoading,
    isUpsellsLoading,
    setIsUpsellsLoading,
    fetchPackages,
    fetchUpsells,
  };

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
}

export function useBooking() {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
}
